<template>
  <div class="section overflow-hidden position-relative min-height" id="wrapper">
    <div class="page-content-wrapper padding-left-0">
      <div class="container-fluid custom-container">
        
        <!-- Breadcrumb Start -->
        <div class="row mb-4">
          <div class="col-12">
            <nav aria-label="breadcrumb">
              <ol class="breadcrumb">
                <li class="breadcrumb-item"><router-link to="/dashboard/user/home">Dashboard</router-link></li>
                <li class="breadcrumb-item active" aria-current="page">Checkout</li>
              </ol>
            </nav>
          </div>
        </div>
        <!-- Breadcrumb End -->

        <div class="row justify-content-center">
          <div class="col-lg-8">
            
            <!-- Loading State -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-3">Loading package details...</p>
            </div>

            <!-- Error State -->
            <div v-if="error" class="alert alert-danger" role="alert">
              <i class="icofont-exclamation-circle me-2"></i>{{ error }}
              <div class="mt-3">
                <router-link to="/dashboard/user/home" class="btn btn-primary">Back to Dashboard</router-link>
              </div>
            </div>

            <!-- Checkout Content -->
            <div v-if="!loading && !error && selectedPackage">
              
              <!-- Order Summary -->
              <div class="card mb-4">
                <div class="card-header bg-primary text-white">
                  <h4 class="mb-0">Order Summary</h4>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-8">
                      <h5 class="card-title">{{ selectedPackage.package_name }}</h5>
                      <ul class="list-unstyled mt-3">
                        <li><i class="icofont-check-circled text-success"></i> {{ selectedPackage.total_questions }} Questions</li>
                        <li><i class="icofont-check-circled text-success"></i> Practice Test Included</li>
                        <li><i class="icofont-check-circled text-success"></i> Final Test Included</li>
                        <li><i class="icofont-check-circled text-success"></i> {{ selectedPackage.mock_attempts_allowed }} Final Test Attempts</li>
                      </ul>
                    </div>
                    <div class="col-md-4 text-end">
                      <h3 class="text-primary">£{{ selectedPackage.sale_price }}</h3>
                      <p class="text-muted">GBP</p>
                    </div>
                  </div>
                  
                  <hr>
                  
                  <!-- Price Breakdown -->
                  <div class="row">
                    <div class="col-8">
                      <strong>Subtotal:</strong>
                    </div>
                    <div class="col-4 text-end">
                      £{{ selectedPackage.sale_price }}
                    </div>
                  </div>
                  <div class="row mt-2">
                    <div class="col-8">
                      <strong>Tax:</strong>
                    </div>
                    <div class="col-4 text-end">
                      £0.00
                    </div>
                  </div>
                  <hr>
                  <div class="row">
                    <div class="col-8">
                      <h5><strong>Total:</strong></h5>
                    </div>
                    <div class="col-4 text-end">
                      <h5 class="text-primary"><strong>£{{ selectedPackage.sale_price }}</strong></h5>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Payment Section -->
              <div class="card" v-show="!paymentProcessing && !paymentSuccess">
                <div class="card-header bg-secondary text-white">
                  <h4 class="mb-0">Payment Details</h4>
                </div>
                <div class="card-body">
                  <!-- Stripe Payment Element -->
                  <div id="payment-element"></div>
                  
                  <div v-if="paymentError" class="alert alert-danger mt-3" role="alert">
                    <i class="icofont-exclamation-triangle me-2"></i>{{ paymentError }}
                  </div>

                  <div class="d-grid gap-2 mt-4">
                    <button 
                      class="btn btn-primary btn-lg" 
                      @click="handlePayment"
                      :disabled="!stripe || paymentProcessing"
                    >
                      <span v-if="!paymentProcessing">
                        <i class="icofont-lock me-2"></i>Pay £{{ selectedPackage.sale_price }}
                      </span>
                      <span v-else>
                        <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Processing...
                      </span>
                    </button>
                    
                    <button 
                      class="btn btn-outline-secondary" 
                      @click="goBack"
                      :disabled="paymentProcessing"
                    >
                      <i class="icofont-arrow-left me-2"></i>Cancel
                    </button>
                  </div>

                  <p class="text-center text-muted mt-3 mb-0">
                    <i class="icofont-lock"></i> Secure payment powered by Stripe
                  </p>
                </div>
              </div>

              <!-- Processing State -->
              <div v-if="paymentProcessing && !paymentSuccess" class="card">
                <div class="card-body text-center py-5">
                  <div class="spinner-border text-primary mb-3" style="width: 3rem; height: 3rem;" role="status">
                    <span class="visually-hidden">Processing...</span>
                  </div>
                  <h4>Processing your payment...</h4>
                  <p class="text-muted">Please do not close this page or press the back button.</p>
                </div>
              </div>

              <!-- Success State -->
              <div v-if="paymentSuccess" class="card">
                <div class="card-body text-center py-5">
                  <div class="text-success mb-3">
                    <i class="icofont-check-circled" style="font-size: 4rem;"></i>
                  </div>
                  <h3 class="text-success">Payment Successful!</h3>
                  <p class="mt-3">Thank you for your purchase. Your subscription has been activated.</p>
                  <p class="text-muted">Transaction ID: {{ paymentIntentId }}</p>
                  <router-link to="/dashboard/user/home" class="btn btn-primary mt-3">
                    <i class="icofont-home me-2"></i>Go to Dashboard
                  </router-link>
                </div>
              </div>

            </div>

          </div>
        </div>

      </div>
    </div>

    <!-- Terms & Conditions Modal -->
    <div v-if="showTncModal" class="tnc-modal-overlay">
      <div class="tnc-modal-content">
        <h4 class="tnc-modal-title">Terms &amp; Conditions</h4>
        <p class="tnc-modal-subtitle">Please review and accept our terms before continuing to payment.</p>

        <div class="tnc-modal-body">
          <h6>1. Acceptance of Terms</h6>
          <p>By purchasing and using this package, you confirm that you have read, understood, and agree to be bound by these terms, our <router-link to="/terms-of-use" target="_blank">Terms of Use</router-link> and <router-link to="/privacy-policy" target="_blank">Privacy Policy</router-link>.</p>

          <h6>2. Payments</h6>
          <p>All payments are processed securely via Stripe. Prices are shown in GBP and are inclusive of any applicable taxes unless stated otherwise. Your subscription will be activated immediately upon successful payment confirmation.</p>

          <h6>3. Refund Policy</h6>
          <p>Payments are generally non-refundable once access to practice or final tests has been used. Refund requests may be considered on a case-by-case basis by contacting our support team within 7 days of purchase.</p>

          <h6>4. Account &amp; Usage</h6>
          <p>Your account and package access are for your personal or organizational use only, as applicable, and may not be shared, resold, or transferred to another party. Misuse of the platform may result in suspension of your account without refund.</p>

          <h6>5. No Guarantee of Results</h6>
          <p>Our practice tests and final tests are designed to help you prepare for the MSQ exam. We do not guarantee any specific exam outcome or certification result.</p>

          <h6>6. Changes to Terms</h6>
          <p>We may update these terms from time to time. Continued use of the platform after changes are made constitutes acceptance of the revised terms.</p>
        </div>

        <label class="tnc-checkbox-label">
          <input type="checkbox" v-model="tncAccepted">
          I have read and agree to the Terms &amp; Conditions
        </label>

        <div class="tnc-modal-actions">
          <button class="btn btn-outline-secondary" @click="declineTnc">Cancel</button>
          <button class="btn btn-primary" :disabled="!tncAccepted" @click="acceptTnc">Continue to Payment</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
/* eslint-disable no-unused-vars */
import { loadStripe } from '@stripe/stripe-js';
import { jwtDecode } from "jwt-decode";
import { getPackageById } from '@/services/packageService';
import { createPaymentIntent, verifyPayment } from '@/services/paymentService';

export default {
  name: 'Checkout',
  data() {
    return {
      selectedPackage: null,
      packageId: null,
      userId: null,
      userEmail: null,
      loading: true,
      error: null,
      stripe: null,
      elements: null,
      clientSecret: null,
      paymentIntentId: null,
      paymentProcessing: false,
      paymentSuccess: false,
      paymentError: null,
      showTncModal: false,
      tncAccepted: false,
    };
  },

  async mounted() {
    console.log('=== CHECKOUT MOUNTED ===');
    
    try {
      this.packageId = this.$route.params.packageId;
      console.log('Package ID:', this.packageId);
      
      if (!this.packageId) {
        this.error = 'Invalid package selection. Please select a package from the dashboard.';
        this.loading = false;
        return;
      }

      const token = localStorage.getItem('access_token');
      console.log('Token exists:', !!token);
      
      if (!token) {
        this.$router.push('/login');
        return;
      }

      const decoded = jwtDecode(token);
      this.userId = decoded.id;
      this.userEmail = decoded.email;
      console.log('User ID:', this.userId);
      console.log('User Email:', this.userEmail);

      console.log('Loading package details...');
      await this.loadPackageDetails();
      console.log('Package loaded:', this.selectedPackage);

      console.log('Initializing Stripe...');

      // Set loading to false before initializing Stripe
      this.loading = false;
      this.showTncModal = true;
      await this.$nextTick();

      await this.initializeStripe();
      console.log('Stripe initialized');

    } catch (err) {
      console.error('=== ERROR IN MOUNTED ===');
      console.error('Error details:', err);
      console.error('Error message:', err.message);
      console.error('Error response:', err.response);
      
      this.error = err.message || 'Failed to initialize checkout. Please try again.';
      this.loading = false;
    }
  },

  methods: {
    async loadPackageDetails() {
      try {
        console.log('Fetching package:', this.packageId);
        const response = await getPackageById(this.packageId);
        console.log('Package response:', response);
        
        if (!response || !response.data) {
          throw new Error('Package not found');
        }
        
        this.selectedPackage = response.data;
        console.log('Package set successfully');
      } catch (err) {
        console.error('Error loading package:', err);
        console.error('Error response:', err.response);
        throw new Error(`Failed to load package: ${err.response?.data?.message || err.message}`);
      }
    },

    async initializeStripe() {
      try {
        console.log('Stripe key exists:', !!process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY);
        
        if (!process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY) {
          throw new Error('Stripe configuration is missing');
        }

        console.log('Loading Stripe...');
        this.stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY);
        console.log('Stripe loaded:', !!this.stripe);
        
        if (!this.stripe) {
          throw new Error('Failed to load Stripe');
        }

        console.log('Creating payment intent with data:', {
          packageId: this.packageId,
          userId: this.userId,
          userEmail: this.userEmail
        });
        
        const response = await createPaymentIntent({
          packageId: this.packageId,
          userId: this.userId,
          userEmail: this.userEmail
        });
        
        console.log('Payment intent response:', response);

        if (!response || !response.data || !response.data.clientSecret) {
          throw new Error('Invalid payment session response');
        }

        this.clientSecret = response.data.clientSecret;
        this.paymentIntentId = response.data.paymentIntentId;
        console.log('Client Secret received');
        console.log('Payment Intent ID:', this.paymentIntentId);

        console.log('Creating Stripe elements...');
        const appearance = {
          theme: 'stripe',
          variables: {
            colorPrimary: '#0066cc',
            borderRadius: '4px',
          }
        };

        this.elements = this.stripe.elements({ 
          clientSecret: this.clientSecret,
          appearance 
        });

        const paymentElement = this.elements.create('payment');
        
        // Wait for DOM
        await this.$nextTick();
        
        paymentElement.mount('#payment-element');

        paymentElement.on('ready', () => {
          console.log('Payment element ready');
        });

      } catch (err) {
        console.error('Error initializing Stripe:', err);
        console.error('Error response:', err.response);
        console.error('Error response data:', err.response?.data);
        throw new Error(`Payment initialization failed: ${err.response?.data?.error || err.message}`);
      }
    },

    async handlePayment() {
      if (!this.stripe || !this.elements) {
        this.paymentError = 'Payment system not ready. Please refresh the page.';
        return;
      }

      this.paymentProcessing = true;
      this.paymentError = null;

      try {
        console.log('Confirming payment...');
        
        const { error, paymentIntent } = await this.stripe.confirmPayment({
          elements: this.elements,
          confirmParams: {
            return_url: `${window.location.origin}/payment-success`,
          },
          redirect: 'if_required'
        });

        if (error) {
          console.error('Payment error:', error);
          this.paymentError = error.message || 'Payment failed. Please try again.';
          this.paymentProcessing = false;
        } else if (paymentIntent && paymentIntent.status === 'succeeded') {
          console.log('Payment succeeded:', paymentIntent);
          await this.verifyAndCreateSubscription();
        } else {
          console.log('Unexpected payment status:', paymentIntent);
          this.paymentError = 'Payment status unclear. Please check your dashboard or contact support.';
          this.paymentProcessing = false;
        }
      } catch (err) {
        console.error('Payment error:', err);
        this.paymentError = 'An unexpected error occurred. Please try again or contact support.';
        this.paymentProcessing = false;
      }
    },

    async verifyAndCreateSubscription() {
      try {
        console.log('Verifying payment...');
        
        const response = await verifyPayment({
          paymentIntentId: this.paymentIntentId,
          userId: this.userId,
          packageId: this.packageId
        });

        console.log('Verification response:', response);

        if (response.data.success) {
          this.paymentSuccess = true;
          this.paymentProcessing = false;
          
          setTimeout(() => {
            this.$router.push('/dashboard/user/home');
          }, 3000);
        } else {
          throw new Error(response.data.message || 'Payment verification failed');
        }
      } catch (err) {
        console.error('Verification error:', err);
        console.error('Error response:', err.response);
        
        this.paymentError = err.response?.data?.message || 
          `Payment verification failed. Your payment was processed but subscription activation failed. Please contact support with transaction ID: ${this.paymentIntentId}`;
        this.paymentProcessing = false;
      }
    },

    goBack() {
      if (confirm('Are you sure you want to cancel? Your order will not be completed.')) {
        this.$router.push('/dashboard/user/home');
      }
    },

    acceptTnc() {
      if (!this.tncAccepted) return;
      this.showTncModal = false;
    },

    declineTnc() {
      this.showTncModal = false;
      this.$router.push('/dashboard/user/home');
    }
  },

  beforeUnmount() {
    if (this.elements) {
      this.elements = null;
    }
  }
};
</script>

<style scoped>
.min-height {
  min-height: 100vh;
}

#payment-element {
  margin-bottom: 24px;
  min-height: 40px;
}

.card {
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.3s ease;
}

.card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.card-header {
  border-radius: 8px 8px 0 0 !important;
}

.breadcrumb {
  background-color: transparent;
  padding: 0;
}

.list-unstyled li {
  padding: 8px 0;
  font-size: 0.95rem;
}

.list-unstyled li i {
  margin-right: 8px;
  font-size: 1.1rem;
}

.btn-lg {
  padding: 12px 24px;
  font-size: 1.1rem;
}

.alert {
  border-radius: 6px;
}

/* Terms & Conditions Modal */
.tnc-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 20px;
  animation: tncFadeIn 0.3s ease;
}

@keyframes tncFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.tnc-modal-content {
  background: #fff;
  border-radius: 10px;
  max-width: 560px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  padding: 30px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

.tnc-modal-title {
  margin-bottom: 4px;
  color: #212832;
  font-weight: 700;
}

.tnc-modal-subtitle {
  color: #666;
  font-size: 14px;
  margin-bottom: 16px;
}

.tnc-modal-body {
  overflow-y: auto;
  max-height: 320px;
  padding-right: 8px;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}

.tnc-modal-body h6 {
  color: #309255;
  font-weight: 700;
  margin-bottom: 4px;
}

.tnc-modal-body p {
  font-size: 14px;
  color: #444;
  margin-bottom: 14px;
  line-height: 1.5;
}

.tnc-modal-body p:last-child {
  margin-bottom: 0;
}

.tnc-checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  margin-bottom: 20px;
}

.tnc-checkbox-label input[type="checkbox"] {
  margin-top: 3px;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  cursor: pointer;
}

.tnc-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.tnc-modal-actions .btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 575px) {
  .tnc-modal-content {
    padding: 20px;
  }
  .tnc-modal-actions {
    flex-direction: column-reverse;
  }
  .tnc-modal-actions .btn {
    width: 100%;
  }
}
</style>